/**
 * The Versioning dialog's edits — a new order and versions marked to become
 * separate files — turned into the RPC calls that apply them, and into the
 * numbers each row will carry afterwards.
 *
 * Mirrors the database exactly (franklin-data-pipeline 20261027105848 and
 * 20261027133600):
 *   detach   renumbers what stays behind 1..n in its existing order, trashed
 *            rows included;
 *   reorder  lets the live rows trade the numbers they then hold, so the last
 *            in the list takes the highest (a legacy duplicate or NULL takes a
 *            fresh number above the lineage's highest, so the numbers
 *            strictly increase).
 * Detaches run first, so the reorder only ever names rows still in the lineage.
 */
import type { LineageRow } from "@/stores/documents/repository";

export interface VersioningPlan {
  /** Versions to make separate files, in list order. */
  detach: number[];
  /** Every live version that stays, oldest first — or null when its order
   *  is unchanged and no reorder call is needed. */
  reorder: number[] | null;
}

/**
 * `original` and `order` are the live versions, oldest first, as loaded and as
 * the user left them.
 */
export function planVersioning(
  original: readonly number[],
  order: readonly number[],
  separated: ReadonlySet<number>
): VersioningPlan {
  const detach = order.filter((id) => separated.has(id));
  const kept = order.filter((id) => !separated.has(id));
  const keptBefore = original.filter((id) => !separated.has(id));
  const unchanged =
    kept.length === keptBefore.length && kept.every((id, i) => id === keptBefore[i]);
  return { detach, reorder: unchanged ? null : kept };
}

/** True when the plan would change anything. */
export function hasVersioningChanges(plan: VersioningPlan): boolean {
  return plan.detach.length > 0 || plan.reorder !== null;
}

const byVersion = (a: LineageRow, b: LineageRow) =>
  (a.version ?? -Infinity) - (b.version ?? -Infinity) || a.id - b.id;

/**
 * The version number each kept live row will carry once the plan is applied.
 * `lineage` is every row of the lineage, trashed ones included. When it is
 * missing a kept row (a fetch that raced a change) nothing is projected: a
 * badge shows a real number or none, never a guess.
 */
export function projectVersionNumbers(
  lineage: readonly LineageRow[],
  order: readonly number[],
  separatedMarks: ReadonlySet<number>
): Map<number, number> {
  // A mark on a row the list no longer has (a refetch dropped it) detaches
  // nothing, so it must not renumber anything either.
  const separated = new Set(order.filter((id) => separatedMarks.has(id)));
  const staying = lineage.filter((row) => !separated.has(row.id)).sort(byVersion);
  // A detach closes what stays up to 1..n first; otherwise rows hold what
  // they hold, NULL included.
  const numberOf = new Map<number, number | null>();
  staying.forEach((row, i) => {
    numberOf.set(row.id, separated.size > 0 ? i + 1 : (row.version ?? null));
  });

  const kept = order.filter((id) => !separated.has(id));
  const projected = new Map<number, number>();
  // A row the lineage read does not know (a fetch that raced a change) has
  // no number to project, so it shows none; the rows it does know keep theirs.
  const known = kept.filter((id) => numberOf.has(id));
  // No reorder call when what stays keeps its order: the numbers stand.
  const knownSet = new Set(known);
  const current = staying.filter((row) => knownSet.has(row.id)).map((row) => row.id);
  if (current.every((id, i) => id === known[i])) {
    // A legacy row with no stored number keeps none: show no badge rather
    // than one the save will never write.
    known.forEach((id) => {
      const number = numberOf.get(id);
      if (number != null) projected.set(id, number);
    });
    return projected;
  }
  // A reordered list with a row the read does not know is not the live set:
  // the save will be refused, so any number projected for it would be a guess.
  if (known.length < kept.length) return projected;
  // A reorder hands out strictly increasing numbers, so legacy duplicates
  // cannot let the database's id tie-break crown another row. A slot that has
  // to change takes a fresh number above every number in the lineage, trashed
  // rows included, so it never lands on one a trashed version holds.
  const slots = kept
    .map((id) => numberOf.get(id) ?? null)
    // Numbers ascending, NULLs last; subtracting two Infinities is NaN.
    .sort((a, b) => (a === b ? 0 : a === null ? 1 : b === null ? -1 : a - b));
  let next = Math.max(0, ...[...numberOf.values()].filter((n): n is number => n !== null));
  const assigned: number[] = [];
  slots.forEach((slot, i) => {
    assigned.push(slot === null || (i > 0 && slot <= assigned[i - 1]) ? ++next : slot);
  });
  kept.forEach((id, i) => projected.set(id, assigned[i]));
  return projected;
}
